import {AfterViewInit, Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild} from '@angular/core'
import {DateTime} from 'luxon'
import {Environment, ENVIRONMENTS, Region} from '../../../services/environment-service'
import {of, Subject} from 'rxjs'
import {debounceTime, distinctUntilChanged, map, mergeMap, switchMap, takeUntil, tap, toArray} from 'rxjs/operators'
import {MatTableDataSource} from '@angular/material/table'
import {ManagementInformationService, MiEvent} from '../../../services/management-information.service'
// import {stringify} from 'csv/lib/sync'
import {MatPaginator} from '@angular/material/paginator'
import { unparse } from 'papaparse'
import saveAs from 'file-saver'
const equals = require('fast-deep-equal')

const environmentLookup = (account) => [ENVIRONMENTS.TOOLS,ENVIRONMENTS.STAGING, ENVIRONMENTS.UAT, ENVIRONMENTS.PROD].filter(env => !!env).find((v) => v && (v.account === account))

interface SmartDxMaintenanceMiEvent extends MiEvent {
  source: 'inspire.auto-deploy-templates'
  detail: {
    requestedBy: string
  }
}

interface FlattenedSmartDxMaintenanceMiEvent {
  timestamp: string
  date: string
  time: string
  environment: string
  region: string
  status: string
  user: string
}

interface Request {
  environment: Environment
  fromDate: string
  toDate: string
}

@Component({
  selector: 'app-smart-dx-maintenance',
  templateUrl: './smart-dx-maintenance.component.html',
  styleUrls: ['./smart-dx-maintenance.component.scss'],
})
export class SmartDxMaintenanceComponent implements OnInit, OnDestroy, OnChanges, AfterViewInit {
  loading = true
  cancelRequests = new Subject<void>()

  @Input() environment: Environment
  @Input() fromDate: string
  @Input() toDate: string

  results: FlattenedSmartDxMaintenanceMiEvent[] = []
  columns: string[] = [
    'date',
    'time',
    'environment',
    'region',
    'status',
    'user'
  ]
  dataSource = new MatTableDataSource<FlattenedSmartDxMaintenanceMiEvent>()
  requests = new Subject<Request>()

  constructor(private managementInformationService: ManagementInformationService) {
  }

  @ViewChild(MatPaginator) paginator: MatPaginator;

  ngAfterViewInit() {
    this.dataSource.paginator = this.paginator;
  }

  ngOnInit(): void {
    // this.loading = true
    this.requests
      .pipe(
        takeUntil(this.cancelRequests),
        debounceTime(1000),
        distinctUntilChanged(equals),
        tap(_ => this.loading = true),
        tap(request => {
          console.log('Processing: ')
          console.dir(request)
        }),
        switchMap((request: Request) => {
          if (request.environment && request.fromDate) {
            const dateRange = request.fromDate + (request.toDate ? '/' + request.toDate : '')
            return of(...request.environment.regions)
              .pipe(
                mergeMap((region: Region) => {
                  console.log('Fetching data for ' + region.display)
                  return this.managementInformationService.getManagementInformation<SmartDxMaintenanceMiEvent[]>(
                    request.environment.id,
                    region.id,
                    'auto-deploy-templates',
                    dateRange)
                    .pipe(
                      tap(results => {
                        console.log('Fetched Mi Data For ' + request.environment.display + ' in ' + region.display)
                        console.dir(results)
                      }),
                    )
                }),
                toArray(),
                tap((results: SmartDxMaintenanceMiEvent[][]) => {
                  console.log('Raw Mi Data')
                  console.dir(results)
                }),
                map((events: SmartDxMaintenanceMiEvent[][]): SmartDxMaintenanceMiEvent[] => {
                  return events.reduce((r, a) => r.concat(...a), <SmartDxMaintenanceMiEvent[]> [])
                }),
                tap((results: any[]) => {
                  console.log('Combined Mi Data')
                  console.dir(results)
                }),
                tap(_ => this.loading = false),
              )

          } else {
            return []
          }
        }),
      )
      .subscribe((events: SmartDxMaintenanceMiEvent[]) => {
        const flattened = events.map(e => this.format(e))
        flattened.sort((a, b) => b.timestamp.localeCompare(a.timestamp))
        this.results = flattened
        this.dataSource.data = this.results
      })
    this.ngOnChanges(undefined)
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.requests.next({
      environment: this.environment,
      fromDate: this.fromDate,
      toDate: this.toDate,
    })
  }

  ngOnDestroy(): void {
    this.cancelRequests.next()
  }

  format(e: SmartDxMaintenanceMiEvent): FlattenedSmartDxMaintenanceMiEvent {
    const environment = environmentLookup(e.account).display
    const region = e.region === 'eu-central-1' ? 'Frankfurt' : 'London'
    const detail = e.detail
    return {
      timestamp: e.time,
      date: DateTime.fromISO(e.time).toFormat('dd/MM/yyyy'),
      time: DateTime.fromISO(e.time).toFormat('HH:mm:ss'),
      environment: environment,
      region: region,
      status: e['detail-type'],
      user: detail.requestedBy
    }
  }

  downloadCsv() {
    const csv = unparse(this.results, {
      header: true,
    })
    const blob = new Blob([csv], {type: 'text/csv;charset=utf-8'})
    const filename = ('mi-smartdx-maintenance-' + this.fromDate + (this.toDate ? '-to-' + this.toDate : '') + '.csv')
    console.log('Saving: ' + filename)
    saveAs(blob, filename)
  }
}
